import { useCallback, useMemo, useState } from "react";

/**
 * Filters for the View Reports modals.
 *
 * `Orders` (backed by the `is_order` column) is EXCLUSIVE: a document flagged as an
 * order matches `Orders` and nothing else — not `All`, and not whatever
 * classification it happens to carry. An order is paperwork to act on rather than a
 * result to read, and it has its own workflow behind the Orders button, so listing it
 * again among the reports only made the reading list harder to scan.
 *
 * `Unclassified` means there is no file_classification_cache row at all — it is a UI
 * label, never a stored value. `OCR Failed` surfaces the OCR_Failed_* values written
 * by pending_fax_recovery.py.
 *
 * `always: false` chips render only when their count > 0; the rest always render so
 * chip positions stay stable between folders.
 */
export const REPORT_FILTERS = [
  { key: "All", label: "All", always: true },
  { key: "Labs", label: "Labs", always: true },
  { key: "Images", label: "Images", always: true },
  { key: "Medical Records", label: "Medical Records", always: true },
  { key: "Orders", label: "Orders", always: true },
  { key: "Other", label: "Other", always: true },
  { key: "Unclassified", label: "Unclassified", always: false },
  { key: "OCR Failed", label: "OCR Failed", always: false },
];

const OCR_FAILED_PREFIX = "OCR_Failed_";

export function matchesFilter(entry, filterKey) {
  if (filterKey === "Orders") return entry?.is_order === true;
  // Everything below this line describes REPORTS. An order is not one, so it
  // leaves here regardless of the classification it carries — including the
  // catch-alls, `Other` and `Unclassified`, which would otherwise re-admit it.
  if (entry?.is_order === true) return false;
  if (filterKey === "All") return true;
  const classification = entry?.classification ?? null;
  if (filterKey === "Unclassified") return classification == null;
  if (filterKey === "OCR Failed") {
    return typeof classification === "string" && classification.startsWith(OCR_FAILED_PREFIX);
  }
  return classification === filterKey;
}

export function countByFilter(entries) {
  const counts = {};
  for (const { key } of REPORT_FILTERS) counts[key] = 0;
  for (const entry of entries || []) {
    for (const { key } of REPORT_FILTERS) {
      if (matchesFilter(entry, key)) counts[key] += 1;
    }
  }
  return counts;
}

export function useReportFilters(entries) {
  const [activeFilter, setActiveFilter] = useState("All");
  const counts = useMemo(() => countByFilter(entries), [entries]);
  const visibleFilters = useMemo(
    () => REPORT_FILTERS.filter((f) => f.always || counts[f.key] > 0),
    [counts]
  );
  const matches = useCallback((entry) => matchesFilter(entry, activeFilter), [activeFilter]);
  return { activeFilter, setActiveFilter, counts, visibleFilters, matches };
}
